import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
import { THEMES, THEME_LIST } from '../theme/themes';
import { useTheme } from '../theme/ThemeProvider';

export const ThemeSwitcher = ({ align = 'right' }) => {
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = THEMES[theme] || THEMES[THEME_LIST[0]];

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="theme-switcher-toggle"
        data-magnetic
        className="group flex items-center gap-3 px-3 py-2 hairline border-subtle hover:border-gold transition-colors"
        aria-label="Change palette"
      >
        <span className="flex items-center">
          {active.swatch.map((c, i) => (
            <span
              key={c + i}
              className="w-[9px] h-[9px] hairline border-subtle"
              style={{ backgroundColor: c, marginLeft: i ? -3 : 0 }}
            />
          ))}
        </span>
        <span className="eyebrow-sm text-mb-muted group-hover:text-gold transition-colors">
          {active.label}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className={`absolute top-[calc(100%+10px)] ${
              align === 'right' ? 'right-0' : 'left-0'
            } w-[290px] bg-mb hairline border-gold z-[60] p-2`}
            data-testid="theme-switcher-panel"
          >
            <div className="eyebrow-sm text-mb-muted px-3 py-3">
              Choose the house palette
            </div>
            {THEME_LIST.map((id) => {
              const t = THEMES[id];
              const isActive = id === theme;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    setTheme(id);
                    setOpen(false);
                  }}
                  data-testid={`theme-option-${id}`}
                  className={`w-full text-left px-3 py-3 flex items-start gap-3 transition-colors ${
                    isActive ? 'bg-mb-surface' : 'hover:bg-mb-surface'
                  }`}
                >
                  <span className="flex mt-[3px] shrink-0">
                    {t.swatch.map((c, i) => (
                      <span
                        key={c + i}
                        className="w-[14px] h-[14px] hairline border-subtle"
                        style={{ backgroundColor: c, marginLeft: i ? -4 : 0 }}
                      />
                    ))}
                  </span>
                  <span className="flex-1">
                    <span className="block font-display text-[15px] text-mb">
                      {t.label}
                    </span>
                    <span className="block mt-1 text-[11px] leading-relaxed text-mb-muted">
                      {t.note}
                    </span>
                  </span>
                  {isActive && (
                    <Check size={13} strokeWidth={1.5} className="text-gold mt-1 shrink-0" />
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ThemeSwitcher;
